/**
 * Ted Item 32.1 — "Performed by" / groomer pickers listed people who have
 * left (Kylie, Gabriel). The staff list endpoint returns every StaffUser on
 * the tenant, active or not (the Staff page needs the inactive ones), so each
 * picker has to choose who it offers.
 *
 * The rule, in one place for every picker:
 *   · offer only staff whose record is active;
 *   · EXCEPT the person already recorded on the row being edited — a leg
 *     Kylie performed last month still says Kylie. Dropping her from the
 *     options would blank that history (the <select> would fall back to
 *     "Nobody yet"), and a save would then silently erase who did the work.
 */
import type { AdminStaffUser } from './api.js';

/** Suffix shown beside a departed staffer kept only because they are on this row. */
export const FORMER_STAFF_SUFFIX = ' (no longer on staff)';

/**
 * Only an explicit `is_active: false` counts as departed. The server always
 * sends the field; treating an absent one as active keeps an older response
 * (or a minimal fixture) on the pre-fix behaviour rather than emptying the
 * picker.
 */
export function isCurrentStaff(s: AdminStaffUser): boolean {
  return s.is_active !== false;
}

/**
 * Active staff, plus the currently-recorded person (if any) even when their
 * record is inactive. Order of the input is preserved.
 */
export function pickableStaff(
  staff: readonly AdminStaffUser[],
  keepId?: string | null,
): AdminStaffUser[] {
  return staff.filter((s) => isCurrentStaff(s) || (keepId != null && keepId !== '' && s.id === keepId));
}

/** "First Last", plus a plain marker when the person has left. */
export function staffOptionLabel(s: AdminStaffUser, base?: string): string {
  const name = base ?? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim();
  return isCurrentStaff(s) ? name : `${name}${FORMER_STAFF_SUFFIX}`;
}
